;; Copyright (C) 2016-2017, Doctor Evidence. All rights reserved.

(ns dre.components.subject
  (:require
   [clojure.string :as str]
   [dre.app.router :as router :refer [->route ->route-with-query-params]]
   [dre.components.icon :refer [fa-icon]]
   [dre.components.tooltip :refer [tooltip-wrapper]]
   [dre.i18n.events :as-alias i18n]
   [dre.pages.search.events :as search-events]
   [dre.panels.events :as-alias panels]
   [dre.page-util :refer [suggested-uri? lower]]
   [dre.shared.interface.search.pills :as pills]
   [dre.util :refer [unkeyword]]
   [dre.utils.bem :as bem]
   [dre.utils.query :as query]
   [re-frame.core :as rf]
   [reagent.core :as r]))

(def component-name :subject)

;;;; Implementation details

(defn suggested-fragment?
  "Returns true if a phrase fragment is a suggestion."
  [fragment]
  (let [{:keys [:is-suggestion :uri :sense]} fragment]
    (or is-suggestion
        (and (not (str/blank? sense))
             (str/starts-with? sense "SX"))
        (suggested-uri? uri))))

(defn disabled-fragment?
  "Returns true if a phrase fragment is a suggestion."
  [fragment opts]
  (let [{:keys [:uri]} fragment
        {:keys [:enable-emtree?]} opts]
    (and (not enable-emtree?)
         (not (str/blank? uri))
         (str/starts-with? uri "https://data.elsevier.com/lifescience/emtree"))))

(defn fragment-label
  "Returns fragment label."
  [fragment opts]
  (let [{:keys [:label :uri]} fragment
        {:keys [:label-map]} opts]
    (or label (get label-map uri) (get label-map (keyword uri)) "…")))

(defn root-fragment?
  "Returns true if given phrase fragment is a root one."
  [fragment phrase]
  (= (:uri fragment) (:uri (first phrase))))


(defn fragment-sense
  "Returns guid string of a given fragment."
  [fragment opts]
  (let [{:keys [:sense]} fragment]
    sense))

(defn fragment-guid
  "Returns guid string of a given fragment."
  [fragment opts]
  (let [{:keys [:guid :uri]} fragment
        {:keys [:guid-map]} opts]
    (or guid (get guid-map uri) (get guid-map (keyword uri)))))


(defn fragment
  "Returns a fragment component."
  [fragment phrase opts]
  (let [{:keys [:uri]} fragment
        {:keys [:href?]} opts
        label (fragment-label fragment opts)
        sense (fragment-sense fragment opts)
        ;; TODO 
        ;; route (routes/dictionary
        ;;        (if (:sense fragment)
        ;;          (ddb/sense-route (fragment-sense fragment opts))
        ;;          (ddb/route (fragment-guid fragment opts))))
        route "#"
        ]
    (with-meta
      (if (or (suggested-fragment? fragment)
              (disabled-fragment? fragment opts))
        [:div
         {:class [:item :label :pill :freetext]}
         [:span label]]
        [:a
         (if href?
           {:class [:item :label :pill :label-primary :selectable]
            :href route}
           {:class [:item :label :pill :label-primary :selectable]
            :on-click
            (fn [_]
              (rf/dispatch
               [::panels/set-panel :sense-detail {:modal-params {:phrase [{:label label
                                                                           :sense sense}]}}]))})
         [:span label]])
      {:key uri})))

(defn phrase-pills
  "Returns query pills for a given subject phrase."
  [phrase opts]
  (let [{:keys [:query/sentence-tag-filter]} opts
        mf #(let [pill (if (suggested-fragment? %)
                         (hash-map :pill/type :text
                                   :pill/query (fragment-label % opts))
                         (hash-map :pill/type :term
                                   :pill/query (fragment-sense % opts)
                                   :pill/label (fragment-label % opts)))]
              (if-not (str/blank? (unkeyword sentence-tag-filter))
                (pills/sentence-tag-pill (unkeyword sentence-tag-filter) pill)
                pill))]
    (vec (remove #(empty? (:pill/query %)) (map mf (distinct phrase))))))

(defn phrase-controls
  "A phrase controls reagent component."
  [phrase query opts]
  (let [filter-key :items-subjects
        pills (phrase-pills phrase opts)
        search-query
        {:query/type :articles
         :query/pills pills}]
    [:<>
     (let [route (query/route search-query nil)]
       [tooltip-wrapper "Search for items" [:a
                                            {:href (->route-with-query-params (->route :search) (:query-params route))}
                                            [fa-icon :magnifying-glass]]])
     (when query
       [tooltip-wrapper "Add into current search query" [:a
                                                         {:on-click #(rf/dispatch [::search-events/widget-filter-select filter-key {:pill/pills pills
                                                                                                                                    :pill/type :and}])}
                                                         [fa-icon :magnifying-glass-plus]]])]))

;;;; Public API

(defn phrase
  "A subject phrase reagent component.
  Supported opts are :guid-map, :label-map, :tags and :hide-controls?"
  [phrase query & opts]
  (let [{:keys [:hide-controls?]} opts
        show-controls? (r/atom false)
        set-controls
        (fn [_ visible?]
          (reset! show-controls? visible?))]
    (r/create-class
     {:reagent-render
      (fn [phrase query & opts]
        [:div
         {:class component-name
          :on-mouse-over #(set-controls % true)
          :on-mouse-leave #(set-controls % false)}
         [:div
          {:class (bem/be component-name :fragment)}
          (let [p* (distinct phrase)]
            ^{:key (str p*)}
            [:<>
             (doall
              (map-indexed
               (fn [idx m]
                 (with-meta
                   (fragment m phrase opts)
                   {:key idx})) p*))]) 
          (when (and (not hide-controls?) @show-controls?)
            [:div
             {:class (bem/be component-name :actions)}
             [phrase-controls phrase query opts]])]])})))
